'use client'
import React, {useState, useRef, useContext} from "react"
import {
  Box,
  useTheme,
  TextField,
  Typography,
  Alert, 
  useMediaQuery,
  Grid, Button
} from "@mui/material"
import Image from "next/image"
import Turnstile from "react-turnstile"
import {GLOBAL_PATHS} from "@/lib/globals"
import {appLinks} from "@/lib/links"
import {isEmail, isPhoneNumber} from "@/lib/libs"
import {TranslationContext} from "@/configs/context"
import { GlassBox } from "@/ui/components/custom/box"


const ContactForm: React.FC = () => {
  const theme = useTheme()
  // @ts-expect-error:next-line
  const { t, lang }: object = useContext(TranslationContext)
  const matchMd = useMediaQuery(theme.breakpoints.up('md'))

  const [pending, setPending] = useState(false)
  const [state, setState] = useState({
    error: '',
    email: '',
    phone: '',
    fullName: '',
    content: '',
    success: false,
    emailError: false,
    phoneError: false,
    contentError: false,
    fullNameError: false,
  })

  const fullNameRef = useRef(null)
  const emailRef = useRef(null)
  const phoneRef = useRef(null)
  const contentRef = useRef(null)

  const handleSubmit = async () => {
    const fullName = fullNameRef.current?.value?.trim()
    const email = emailRef.current?.value?.trim()
    const phone = phoneRef.current?.value?.trim()
    const content = contentRef.current?.value?.trim()

    if (fullName.length === 0) {
      return setState({
        ...state,
        fullNameError: true,
        error: t('translation:error.contact_name_error')
      })
    }
    setState({ ...state, error: '', success: false, fullNameError: false })

    if (!isEmail(email)) {
      return setState({
        ...state,
        emailError: true,
        error: t('translation:error.contact_email_error')
      })
    }
    setState({ ...state, emailError: false })

    if (phone.length > 0 && !isPhoneNumber(phone)) {
      setState({
        ...state,
        phoneError: true,
        error: t('translation:error.contact_phone_error')
      })
    }
    setState({ ...state, phoneError: false })

    if (content.length === 0) {
      return setState({
        ...state,
        contentError: true,
        error: t('translation:error.contact_content_error')
      })
    }
    setState({ ...state, contentError: false })
    setPending(true)

    try {
      const data = await fetch(
        appLinks.api.CONTACT,
        {
          method: 'post',
          body: JSON.stringify({ fullName, email, phone, content }),
          headers: {'Content-Type': 'application/json'}
        }
      )

      const result = await data.json()
      setPending(false)

      if (result?.success) {
        return setState({
          ...state,
          error: '',
          email: '',
          phone: '',
          content: '',
          fullName: '',
          success: true
        })
      }

      // Si la requête n'a pas abouti, on affiche un message d'erreur
      setState({
        ...state,
        success: false,
        error: result?.reason ?? t('translation:error.contact_generic_error'),
      })

    } catch (e) {
      console.log(e)
      setPending(false)
      setState({
        ...state,
        success: false,
        error: t('translation:error.contact_generic_error'),
      })
    }
  }

  const handleFullNameChange = (event) => {
    const fullName = event.target.value?.toUpperCase()

    setState({ ...state, fullNameError: false, fullName })
    if (fullName.length === 0) {
      setState({ ...state, fullName, fullNameError: true })
    }
  }

  const handleEmailChange = (event) => {
    const email = event.target.value?.trim()

    if (email.length > 1 && !isEmail(email)) {
      return setState({ ...state, email, emailError: true })
    }

    setState({ ...state, emailError: false, email })
  }

  const handlePhoneChange = (event) => {
    const phone = event.target.value?.trim()

    if (phone.length === 0 || !isPhoneNumber(phone)) {
      return setState({ ...state, phone, phoneError: true })
    }

    setState({ ...state, phoneError: false, phone })
  }

  const handleContentChange = (event) => {
    setState({
      ...state,
      contentError: false,
      content: event.target.value
    })
  }

  // Gestion de la touche Entrée pour soumettre le formulaire
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  // Gestion spéciale pour le champ multiline (contenu)
  const handleContentKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
    // Shift+Enter permet d'ajouter une nouvelle ligne (comportement par défaut)
  }

  let imageSize = 18*16
  if (matchMd) { imageSize = 24*16 }

  return (
    <React.Suspense fallback={<div />}>
      <GlassBox 
        sx={{ 
          my: 8, 
          py: 2, px: 2, 
          color: "text.primary"
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          id="contact-section"
          justifyContent="space-between"
        >
          <Grid size={{ xs: 12, md: 8, lg: 7, xl: 6 }}>
            <Typography variant="h4" sx={{ marginBottom: "2rem" }}>
              {t('translation:contact.title')}
            </Typography>
            <TextField
              required
              fullWidth
              type="text"
              id="fullName"
              name="fullName"
              variant="outlined"
              color="primary"
              inputRef={fullNameRef}
              error={state?.fullNameError}
              value={state?.fullName ?? ''}
              sx={{ marginBottom: "1rem" }}
              onChange={handleFullNameChange}
              onKeyDown={handleKeyDown}
              label={t('translation:contact.full_name')}
            />
            <Box sx={{ display: matchMd ? "flex" : "default", gap: "16px" }}>
              <TextField
                required
                fullWidth
                id="email"
                type="email"
                name="email"
                variant="outlined"
                color="primary"
                inputRef={emailRef}
                error={state?.emailError}
                value={state?.email ?? ''}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                sx={{ marginBottom: "1rem" }}
                label={t('translation:contact.email')}
              />
              <TextField
                fullWidth
                id="phone"
                type="tel"
                name="phone"
                variant="outlined"
                color="primary"
                inputRef={phoneRef}
                error={state?.phoneError}
                value={state?.phone ?? ''}
                onChange={handlePhoneChange}
                onKeyDown={handleKeyDown}
                sx={{ marginBottom: "1rem" }}
                placeholder="(+237) 6xx xxx xxx"
                label={t('translation:contact.phone')}
              />
            </Box>
            <TextField
              rows={4}
              required
              fullWidth
              multiline
              type="text"
              id="content"
              name="content"
              variant="outlined"
              color="primary"
              inputRef={contentRef}
              error={state?.contentError}
              value={state?.content ?? ''}
              onChange={handleContentChange}
              onKeyDown={handleContentKeyDown}
              label={t('translation:contact.content')}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: `${theme.spacing(4)} !important`, 
                },
                marginBottom: "1rem",
                "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                  marginLeft: "8px",
                  // background: theme.palette.background.default
                }
              }}
            />
            {state?.error !== "" &&
              <Alert severity="error" sx={{ mt: 0, mb: 1.5 }}>
                {state?.error}
              </Alert>}
            {state?.success &&
              <Alert severity="success" sx={{ mt: 0, mb: 1.5 }}>
                {t('translation:contact.success_message')}
              </Alert>}

            {process.env.NODE_ENV === "production" && 
            <Turnstile
              theme="auto"
              language={lang}
              className="clfr-turnstile-wrapper"
              sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSITE_SITE_KEY}
            />}
            
            <Button
              fullWidth
              size="large"
              type="submit"
              disableElevation
              loading={pending}
              variant="black-glass"
              onClick={handleSubmit}
              sx={{ mt: 1, height: 48 }}
            >
              {t('translation:contact.submit')}
            </Button>
          </Grid>

          <Grid
            sx={{ textAlign: "right" }}
            size={{ xs: 12, md: 8, lg: 5, xl: 4 }}
          >
            <Image
              alt="Contact"
              width={imageSize}
              height={imageSize}
              style={{ borderRadius: "16px" }}
              src={`${GLOBAL_PATHS.IMAGES}4537998.webp`}
            />
          </Grid>
        </Grid>
      </GlassBox>
    </React.Suspense>
  )
}

export default ContactForm
